/**
 * The visit page's reads and writes — Slice 1's surface, fixed so every tab
 * codes against it (`docs/visit-page.md` §5.2; lead decisions, round 1 item 1).
 *
 * - `useVisitRead(id)` — `GET /visits/:id`: the header, the tabs and their
 *   counts, the Overview and the rail (`VisitRead`).
 * - `useVisitTab<T>(id, path, query?)` — a tab's own read (`path` like
 *   `'/materials'`).
 * - `useVisitWrite(id)` — `write(method, path, body, { tab? })`: every write
 *   answers the fresh read of the tab it came from **plus the header and the
 *   counts** (§5.2's last line, `VisitWriteAnswer`); this hook hands the header,
 *   the counts and the status to the page (through `VisitPageProvider`) and
 *   gives the caller the tab.
 *
 * Path rules (round 1 item 1): a path starting with `/punches/`, `/receipts/`,
 * `/visit-photos/`, `/purchase-orders/` or `/crew-notes/` is the API's own
 * (under `/api/v1`); anything else is the visit's (`/visits/:id` + path). `tab`
 * goes on as `?tab=<key>` and names the tab whose read comes back.
 *
 * Nothing here computes a number: the page renders what the server sent.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type {
  VisitHeader, VisitPageStatus, VisitRead, VisitTabCount, VisitTabKey, VisitTabRead, VisitWriteAnswer,
} from '@rowted/shared'
import { request } from '../lib/api.js'

/** The API's own paths, used as they are rather than under `/visits/:id`. */
const OWN_PATHS = ['/punches/', '/receipts/', '/visit-photos/', '/purchase-orders/', '/crew-notes/'] as const

/** Where a visit path goes, under `/api/v1`. */
export function visitPath(id: string, path: string): string {
  if (OWN_PATHS.some((prefix) => path.startsWith(prefix))) return path
  return `/visits/${encodeURIComponent(id)}${path === '' || path.startsWith('/') || path.startsWith('?') ? path : `/${path}`}`
}

function withQuery(path: string, query?: Record<string, string | number | boolean | null | undefined>): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const text = params.toString()
  if (!text) return path
  return `${path}${path.includes('?') ? '&' : '?'}${text}`
}

// ---------------------------------------------------------------------------
// The page's own state — what a write hands back to
// ---------------------------------------------------------------------------

export interface VisitPageState {
  id: string
  read: VisitRead
  /** Replace the whole read (a write from the Overview, a refetch). */
  setRead: (next: VisitRead) => void
  /** A write's header, counts and status, onto the read the page draws. */
  apply: (answer: VisitWriteAnswer) => void
}

const VisitPageContext = createContext<VisitPageState | null>(null)

/** The page puts its read here so a tab's write can move the header and the counts (§5.2). */
export function VisitPageProvider({ value, children }: { value: VisitPageState; children: ReactNode }) {
  return createElement(VisitPageContext.Provider, { value }, children)
}

/** The page's read and its setters — null outside the visit page (a tab drawn on its own in a test). */
export function useVisitPage(): VisitPageState | null {
  return useContext(VisitPageContext)
}

/** Folds a write's answer into a read: the header, the counts, the status — and the whole read when the tab is the Overview. */
export function applyAnswer(read: VisitRead, answer: VisitWriteAnswer): VisitRead {
  if (answer.tab.key === 'overview') return answer.tab.read
  return { ...read, header: answer.header, tabs: answer.tabs, status: answer.status }
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface VisitReadState {
  data?: VisitRead
  /** The error's message when the read failed (404 for a visit that is not there). */
  error?: string
  /** Refetch, or take the given read as the new one (a write's answer). */
  refresh: (next?: VisitRead) => void
}

/** `GET /visits/:id` — `id` is a work item's id or reference (*V-1187*). */
export function useVisitRead(id: string): VisitReadState {
  const [data, setData] = useState<VisitRead | undefined>()
  const [error, setError] = useState<string | undefined>()
  const latest = useRef(0)

  const load = useCallback(() => {
    const ticket = ++latest.current
    request<VisitRead>(visitPath(id, ''))
      .then((body) => { if (ticket === latest.current) { setData(body); setError(undefined) } })
      .catch((cause: unknown) => {
        if (ticket === latest.current) setError(cause instanceof Error ? cause.message : String(cause))
      })
  }, [id])

  useEffect(() => { load() }, [load])

  const refresh = useCallback((next?: VisitRead) => {
    if (next) { latest.current += 1; setData(next); setError(undefined) } else load()
  }, [load])

  return { data, error, refresh } as VisitReadState
}

export interface VisitTabState<T> {
  data?: T
  loading: boolean
  error?: string
  /** Refetch, or take the given read (a write's `tab`). */
  refresh: (next?: T) => void
}

/** A tab's own read — `useVisitTab<MaterialsRead>(id, '/materials', { view: 'status' })`. */
export function useVisitTab<T>(
  id: string,
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): VisitTabState<T> {
  const [data, setData] = useState<T | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const latest = useRef(0)
  const url = withQuery(visitPath(id, path), query)

  const load = useCallback(() => {
    const ticket = ++latest.current
    setLoading(true)
    request<T>(url)
      .then((body) => { if (ticket === latest.current) { setData(body); setError(undefined) } })
      .catch((cause: unknown) => {
        if (ticket === latest.current) setError(cause instanceof Error ? cause.message : String(cause))
      })
      .finally(() => { if (ticket === latest.current) setLoading(false) })
  }, [url])

  useEffect(() => { load() }, [load])

  const refresh = useCallback((next?: T) => {
    if (next !== undefined) { latest.current += 1; setData(next); setLoading(false); setError(undefined) } else load()
  }, [load])

  return { data, loading, error, refresh } as VisitTabState<T>
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export interface VisitWriteResult {
  /** The fresh read of the tab named by `tab` (the Overview's `VisitRead` by default). */
  tab?: VisitTabRead['read']
  header?: VisitHeader
  /** The tab counts — `VisitWriteAnswer.tabs`. */
  counts?: VisitTabCount[]
  status?: VisitPageStatus
  /** The whole answer, as the server sent it. */
  answer?: VisitWriteAnswer
}

export type VisitWrite = (
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  body?: unknown,
  options?: { tab?: VisitTabKey; idempotencyKey?: string },
) => Promise<VisitWriteResult>

/** Is this a `VisitWriteAnswer` (header + tabs + status + tab)? A few writes answer their own shape. */
function isWriteAnswer(value: unknown): value is VisitWriteAnswer {
  return typeof value === 'object' && value !== null
    && 'header' in value && 'tabs' in value && 'tab' in value && 'status' in value
}

/**
 * `write(method, path, body, { tab })` — the answer's header, counts and
 * status go to the page; the tab's read comes back to the caller.
 */
export function useVisitWrite(id: string): VisitWrite {
  const page = useVisitPage()
  const pageRef = useRef(page)
  pageRef.current = page

  return useCallback<VisitWrite>(async (method, path, body, options = {}) => {
    const url = withQuery(visitPath(id, path), options.tab ? { tab: options.tab } : undefined)
    const answer = await request<unknown>(url, {
      method,
      ...(body !== undefined ? { body } : {}),
      ...(options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
    })
    if (!isWriteAnswer(answer)) return { tab: answer as VisitTabRead['read'] }
    pageRef.current?.apply(answer)
    return { tab: answer.tab.read, header: answer.header, counts: answer.tabs, status: answer.status, answer }
  }, [id])
}
